import type { Feature } from 'geojson'
import { useEffect, useMemo, useRef } from 'react'
import { useMap } from '@vis.gl/react-google-maps'
import {
  aDrawMode,
  aDrawnFeature,
  aMapDrawIsOpen,
  useAtom,
} from '~/atoms'
import {
  clearRememberedDrawnFeatureForBackNavigation,
  rememberDrawnFeatureForBackNavigation,
} from './drawnFeatureBackNavigation'
import { useDrawStyleTokens } from './drawStyleTokens'
import {
  circleToFeature,
  featureToCircleOptions,
  isStoredCircleFeature,
} from './googleCircleFeature'

const MIN_PREVIEW_RADIUS_METERS = 1
const METERS_PER_MILE = 1609.344
const FEET_PER_METER = 3.28084

type CircleRadiusLabelOverlay = google.maps.OverlayView & {
  setPosition: (position: google.maps.LatLngLiteral) => void
  setText: (text: string) => void
}

type CircleRadiusLabelOverlayConstructor = new (
  position: google.maps.LatLngLiteral,
  text: string
) => CircleRadiusLabelOverlay

let CircleRadiusLabelOverlayClass:
  | CircleRadiusLabelOverlayConstructor
  | undefined

function formatCircleRadius(radiusMeters: number) {
  if (radiusMeters >= METERS_PER_MILE / 10) {
    return `${(radiusMeters / METERS_PER_MILE).toFixed(1)} mi`
  }

  return `${Math.round(radiusMeters * FEET_PER_METER)} ft`
}

function getCircleRadiusLabelOverlayClass() {
  if (CircleRadiusLabelOverlayClass) return CircleRadiusLabelOverlayClass

  class RuntimeCircleRadiusLabelOverlay extends google.maps.OverlayView {
    private element: HTMLDivElement | null = null
    private position: google.maps.LatLngLiteral
    private text: string

    constructor(position: google.maps.LatLngLiteral, text: string) {
      super()
      this.position = position
      this.text = text
    }

    override onAdd() {
      const element = document.createElement('div')

      element.className =
        'pointer-events-none absolute rounded-md bg-background px-2 py-1 text-xs font-semibold text-foreground shadow-md ring-1 ring-border'
      element.textContent = this.text
      this.element = element
      this.getPanes()?.floatPane.appendChild(element)
    }

    override draw() {
      const element = this.element
      const projection = this.getProjection()

      if (!element || !projection) return

      const point = projection.fromLatLngToDivPixel(
        new google.maps.LatLng(this.position)
      )

      if (!point) return

      element.style.left = `${point.x}px`
      element.style.top = `${point.y}px`
      element.style.transform = 'translate(-50%, -50%)'
    }

    override onRemove() {
      this.element?.remove()
      this.element = null
    }

    setPosition(position: google.maps.LatLngLiteral) {
      this.position = position
      this.draw()
    }

    setText(text: string) {
      this.text = text

      if (this.element) {
        this.element.textContent = text
      }
    }
  }

  CircleRadiusLabelOverlayClass = RuntimeCircleRadiusLabelOverlay

  return CircleRadiusLabelOverlayClass
}

function createCircleRadiusLabelOverlay(
  position: google.maps.LatLngLiteral,
  text: string
): CircleRadiusLabelOverlay {
  const OverlayClass = getCircleRadiusLabelOverlayClass()

  return new OverlayClass(position, text)
}

function distanceMeters(
  start: google.maps.LatLngLiteral,
  end: google.maps.LatLngLiteral
) {
  const toRadians = (value: number) => (value * Math.PI) / 180
  const earthRadiusMeters = 6371000
  const dLat = toRadians(end.lat - start.lat)
  const dLng = toRadians(end.lng - start.lng)
  const startLat = toRadians(start.lat)
  const endLat = toRadians(end.lat)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(startLat) * Math.cos(endLat) * Math.sin(dLng / 2) ** 2

  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function getDefaultCircleOptions(
  map: google.maps.Map,
  styles: ReturnType<typeof useDrawStyleTokens>,
  options?: { clickable?: boolean }
): google.maps.CircleOptions {
  return {
    map,
    clickable: options?.clickable ?? true,
    draggable: false,
    editable: false,
    fillColor: styles.fillColor,
    fillOpacity: styles.fillOpacity,
    strokeColor: styles.outlineColor,
    strokeOpacity: 1,
    strokeWeight: styles.outlineWidth,
  }
}

export function useGoogleCircleDraw() {
  const map = useMap()
  const drawStyles = useDrawStyleTokens()
  const [drawMode, setDrawMode] = useAtom(aDrawMode)
  const [drawnFeature, setDrawnFeature] = useAtom(aDrawnFeature)
  const [, setIsDrawOpen] = useAtom(aMapDrawIsOpen)
  const circleRef = useRef<google.maps.Circle | null>(null)
  const circleListenersRef = useRef<google.maps.MapsEventListener[]>([])
  const drawListenersRef = useRef<google.maps.MapsEventListener[]>([])
  const previewCenterRef = useRef<google.maps.LatLngLiteral | null>(null)
  const radiusLabelRef = useRef<CircleRadiusLabelOverlay | null>(null)
  const isSyncingRef = useRef(false)
  const hasPendingCircleFeatureRef = useRef(false)
  const featureIdRef = useRef<Feature['id'] | undefined>(undefined)

  const clearCircleListeners = () => {
    circleListenersRef.current.forEach((listener) => listener.remove())
    circleListenersRef.current = []
  }

  const removeRadiusLabel = () => {
    radiusLabelRef.current?.setMap(null)
    radiusLabelRef.current = null
  }

  const clearDrawListeners = () => {
    drawListenersRef.current.forEach((listener) => listener.remove())
    drawListenersRef.current = []
    previewCenterRef.current = null
    hasPendingCircleFeatureRef.current = false
    removeRadiusLabel()
  }

  const updateRadiusLabel = (
    center: google.maps.LatLngLiteral,
    radiusMeters: number
  ) => {
    if (!map) return

    const text = formatCircleRadius(radiusMeters)

    if (!radiusLabelRef.current) {
      radiusLabelRef.current = createCircleRadiusLabelOverlay(center, text)
      radiusLabelRef.current.setMap(map)
      return
    }

    radiusLabelRef.current.setPosition(center)
    radiusLabelRef.current.setText(text)
  }

  const removeCircleOverlay = () => {
    clearCircleListeners()
    hasPendingCircleFeatureRef.current = false
    const circle = circleRef.current

    if (!circle) return

    circle.setMap(null)
    circleRef.current = null
  }

  const markCircleFeaturePending = () => {
    if (isSyncingRef.current) return

    hasPendingCircleFeatureRef.current = true
  }

  const syncPendingFeatureFromCircle = () => {
    const circle = circleRef.current

    if (!circle || isSyncingRef.current) return
    if (!hasPendingCircleFeatureRef.current) return

    const feature = circleToFeature(circle, featureIdRef.current)
    rememberDrawnFeatureForBackNavigation(feature)
    setDrawnFeature(feature)
    hasPendingCircleFeatureRef.current = false
  }

  const ensureCircle = () => {
    if (!map) return null

    if (!circleRef.current) {
      circleRef.current = new google.maps.Circle(
        getDefaultCircleOptions(map, drawStyles)
      )
    }

    return circleRef.current
  }

  useEffect(() => {
    if (!map) return

    const circle = ensureCircle()

    if (!circle) return

    clearCircleListeners()

    circleListenersRef.current = [
      circle.addListener('click', () => {
        if (!isStoredCircleFeature(drawnFeature)) return

        setIsDrawOpen(true)
        setDrawMode('draw_circle')
      }),
      circle.addListener('center_changed', markCircleFeaturePending),
      circle.addListener('dragend', syncPendingFeatureFromCircle),
      circle.addListener('radius_changed', markCircleFeaturePending),
      circle.addListener('mouseup', syncPendingFeatureFromCircle),
      map.addListener('mouseup', syncPendingFeatureFromCircle),
    ]

    return () => {
      clearCircleListeners()
    }
  }, [
    drawMode,
    drawStyles,
    drawnFeature,
    map,
    setDrawMode,
    setDrawnFeature,
    setIsDrawOpen,
  ])

  useEffect(() => {
    if (!map) return

    if (!isStoredCircleFeature(drawnFeature)) {
      featureIdRef.current = undefined
      if (drawMode !== 'draw_circle' || previewCenterRef.current === null) {
        removeCircleOverlay()
      }
      return
    }

    if (drawMode !== 'draw_circle') {
      removeCircleOverlay()
      return
    }

    const circle = ensureCircle()
    if (!circle) return

    featureIdRef.current = drawnFeature.id
    const { center, radiusMeters } = featureToCircleOptions(drawnFeature)

    isSyncingRef.current = true
    circle.setOptions({
      ...getDefaultCircleOptions(map, drawStyles, { clickable: true }),
      center,
      radius: radiusMeters,
      visible: true,
    })
    circle.setMap(map)
    isSyncingRef.current = false
  }, [drawMode, drawStyles, drawnFeature, map])

  useEffect(() => {
    const circle = circleRef.current

    if (!circle) return

    const isCircleFeature = isStoredCircleFeature(drawnFeature)
    const isEditingCircle = isCircleFeature && drawMode === 'draw_circle'

    circle.setEditable(isEditingCircle)
    circle.setDraggable(isEditingCircle)
  }, [drawMode, drawnFeature])

  useEffect(() => {
    clearDrawListeners()

    if (
      !map ||
      drawMode !== 'draw_circle' ||
      isStoredCircleFeature(drawnFeature)
    ) {
      return
    }

    const circle = ensureCircle()
    if (!circle) return

    drawListenersRef.current = [
      map.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
        const center = previewCenterRef.current

        if (!center || !event.latLng) return

        const nextRadius = Math.max(
          MIN_PREVIEW_RADIUS_METERS,
          distanceMeters(center, {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          })
        )

        isSyncingRef.current = true
        circle.setRadius(nextRadius)
        isSyncingRef.current = false
        updateRadiusLabel(center, nextRadius)
      }),
      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (!event.latLng) return

        const latLng = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        }

        if (!previewCenterRef.current) {
          previewCenterRef.current = latLng
          isSyncingRef.current = true
          circle.setOptions({
            ...getDefaultCircleOptions(map, drawStyles, { clickable: false }),
            center: latLng,
            radius: MIN_PREVIEW_RADIUS_METERS,
          })
          circle.setMap(map)
          isSyncingRef.current = false
          updateRadiusLabel(latLng, MIN_PREVIEW_RADIUS_METERS)
          return
        }

        const feature = circleToFeature(circle)
        rememberDrawnFeatureForBackNavigation(feature)
        hasPendingCircleFeatureRef.current = false
        setDrawnFeature(feature)
        setDrawMode('draw_circle')
        setIsDrawOpen(true)
        previewCenterRef.current = null
        clearDrawListeners()
      }),
    ]

    return () => {
      if (
        !isStoredCircleFeature(drawnFeature) &&
        previewCenterRef.current === null
      ) {
        removeCircleOverlay()
      }
      clearDrawListeners()
    }
  }, [
    drawMode,
    drawStyles,
    drawnFeature,
    map,
    setDrawMode,
    setDrawnFeature,
    setIsDrawOpen,
  ])

  useEffect(() => {
    if (drawMode === 'draw_circle') return

    clearDrawListeners()
  }, [drawMode])

  useEffect(() => {
    if (
      !map ||
      drawMode !== 'draw_circle' ||
      !isStoredCircleFeature(drawnFeature)
    ) {
      return
    }

    const listener = map.addListener(
      'click',
      (event: google.maps.MapMouseEvent) => {
        const circle = circleRef.current

        if (!circle || !event.latLng) return

        const center = circle.getCenter()
        const radius = circle.getRadius()

        if (!center) return

        const clickedInsideCircle =
          distanceMeters(
            {
              lat: center.lat(),
              lng: center.lng(),
            },
            {
              lat: event.latLng.lat(),
              lng: event.latLng.lng(),
            }
          ) <= radius

        if (clickedInsideCircle) return

        const feature = circleToFeature(circle, featureIdRef.current)
        rememberDrawnFeatureForBackNavigation(feature)
        hasPendingCircleFeatureRef.current = false
        setDrawnFeature(feature)
        circle.setEditable(false)
        circle.setDraggable(false)
        setDrawMode(null)
        setIsDrawOpen(true)
      }
    )

    return () => {
      listener.remove()
    }
  }, [drawMode, drawnFeature, map, setDrawMode, setDrawnFeature, setIsDrawOpen])

  return useMemo(
    () => ({
      canDrawCircle: Boolean(map),
      clearCircle: () => {
        clearRememberedDrawnFeatureForBackNavigation()
        clearDrawListeners()
        removeCircleOverlay()

        if (isStoredCircleFeature(drawnFeature)) {
          setDrawnFeature(null)
          setDrawMode(null)
        }
      },
      startCircleDraw: () => {
        setDrawMode('draw_circle')
        setIsDrawOpen(true)
      },
      stopCircleDraw: () => {
        clearDrawListeners()
        const circle = circleRef.current

        if (circle) {
          circle.setEditable(false)
          circle.setDraggable(false)
        }
      },
    }),
    [drawnFeature, map, setDrawMode, setDrawnFeature, setIsDrawOpen]
  )
}
